import { useState } from 'react';

import { graphql } from '@apollo/client/react/hoc';
import to from 'await-to-js';
import gql from 'graphql-tag';
import compose from 'lodash.flowright';
import PropTypes from 'prop-types';

import { DialogDescription, DialogHeader, DialogTitle } from '@/shadcn/dialog';

import { notify } from '@/components/toast';
import TraceIconSpinner from '@/components/ui/traceIconSpinner';
import { SpanType, withSpanAsync } from '@/tracing';
import mutations from './mutations';
import { RadioButtonsGroup } from './radioButtonsGroup/radioButtonsGroup';

const enhanceSubscriptions = (subscriptions, actions) =>
  subscriptions.map(subscription => {
    const foundAction = actions.find(
      action => action.key === subscription.actionkey
    );
    const enhancedSubscription = {
      ...subscription,
      actionTitle: foundAction?.title ?? subscription.actionkey,
      loading: false,
      showSuccess: false
    };
    return enhancedSubscription;
  });

const SubscriptionsMenu = ({
  userId,
  workflowId,
  actions,
  subscriptionsList,
  updateNotificationSubscription
}) => {
  if (subscriptionsList?.loading) {
    return <TraceIconSpinner />;
  }

  const {
    workflows: { nodes }
  } = subscriptionsList;
  const currentWf = nodes[0];
  const subscriptions = currentWf.notificationSubscriptionsUpdated.nodes;

  const [currentSubscriptions, setSubscriptions] = useState(() =>
    enhanceSubscriptions(subscriptions, actions)
  );

  const updateSubscription = (index, newSubscription) => {
    setSubscriptions(prevSubscriptions => {
      const updatedSubscriptions = [...prevSubscriptions];
      updatedSubscriptions[index] = {
        ...prevSubscriptions[index],
        ...newSubscription
      };
      return updatedSubscriptions;
    });
  };

  const executeTransaction = async (subscription, actionkey) => {
    const [err] = await to(
      updateNotificationSubscription({
        variables: {
          userId,
          workflowId,
          actionkey,
          subscription
        }
      })
    );
    return err;
  };

  const handleTabClick = (clickedSubscription, actionkey) => {
    const newSubscription = clickedSubscription.toUpperCase();

    const indexToUpdate = currentSubscriptions.findIndex(
      s => s.actionkey === actionkey
    );

    const prevSubscription = currentSubscriptions[indexToUpdate].subscription;
    if (prevSubscription === newSubscription) {
      return null;
    }

    updateSubscription(indexToUpdate, {
      subscription: newSubscription,
      loading: true
    });

    return withSpanAsync(
      'updateNotificationSubscription',
      SpanType.processing,
      async () => {
        const promise = executeTransaction(newSubscription, actionkey);
        notify.promise(promise, {
          loading: 'Updating notification subscription...',
          success: 'Notification subscription updated',
          error: 'Notification subscription update failed'
        });
        const err = await executeTransaction(newSubscription, actionkey);

        if (err) {
          updateSubscription(indexToUpdate, {
            subscription: prevSubscription,
            loading: false
          });
        } else {
          // Faking update in case showSuccess is already true
          updateSubscription(indexToUpdate, {
            subscription: newSubscription,
            loading: false,
            showSuccess: false
          });
          updateSubscription(indexToUpdate, {
            subscription: newSubscription,
            loading: false,
            showSuccess: true
          });
        }
      }
    );
  };

  return (
    <DialogHeader>
      <DialogTitle>Notification subscriptions</DialogTitle>
      <DialogDescription className="mb-2">
        <ul className="list-inside list-disc text-xs">
          <li>
            <b>Off:</b> You will never receive any notifications for the given
            action.
          </li>
          <li>
            <b>Default:</b> You will receive notifications as they are
            predefined in the workflow.
          </li>
          <li>
            <b>Always:</b> You will get notified any time the action has been
            performed.
          </li>
        </ul>
      </DialogDescription>
      <section className="scrollbar-thin flex max-h-[275px] flex-col flex-nowrap overflow-y-auto text-sm">
        {currentSubscriptions.length === 0 && (
          <p className="text-center text-sm">No subscriptions</p>
        )}

        {currentSubscriptions.map(sub => (
          <div
            className="flex items-center justify-between gap-5 border-border py-2 text-sm"
            key={sub.actionkey}
          >
            <div className="text-sm">{sub.actionTitle}</div>
            <RadioButtonsGroup
              loading={sub.loading}
              onChange={subscription =>
                handleTabClick(subscription, sub.actionkey)
              }
              tabs={['Off', 'Custom', 'Always']}
              value={sub.subscription}
            />
          </div>
        ))}
      </section>
    </DialogHeader>
  );
};

SubscriptionsMenu.propTypes = {
  userId: PropTypes.string.isRequired,
  workflowId: PropTypes.string.isRequired,
  subscriptionsList: PropTypes.object.isRequired,
  actions: PropTypes.array.isRequired,
  updateNotificationSubscription: PropTypes.func.isRequired
};

const GET_NOTIFICATION_SUBSCRIPTIONS = gql`
  query GetNotificationSubscriptionsUpdated($workflowId: BigInt!) {
    workflows(filter: { rowId: { equalTo: $workflowId } }) {
      nodes {
        rowId
        notificationSubscriptionsUpdated {
          nodes {
            actionkey
            rowId
            subscription
          }
        }
      }
    }
  }
`;

export default compose(
  graphql(mutations.updateNotificationSubscription, {
    name: 'updateNotificationSubscription'
  }),
  graphql(GET_NOTIFICATION_SUBSCRIPTIONS, {
    name: 'subscriptionsList',
    options: ({ workflowId }) => ({
      variables: { workflowId }
    })
  })
)(SubscriptionsMenu);
